import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Navigation, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

const DriverDeliveryApp = () => {
  const { websiteId } = useParams();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get('token') || localStorage.getItem(`driver_token_${websiteId}`) || '');
  const [orderId, setOrderId] = useState('');
  const [status, setStatus] = useState('en_route');
  const [coords, setCoords] = useState(null);
  const [watching, setWatching] = useState(false);

  useEffect(() => {
    if (!watching || !navigator.geolocation) return undefined;
    const id = navigator.geolocation.watchPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => toast.error('Unable to read GPS location'),
      { enableHighAccuracy: true, maximumAge: 10000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [watching]);

  useEffect(() => {
    if (!watching || !token) return undefined;
    const timer = setInterval(() => {
      if (coords) pingLocation(true);
    }, 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watching, token, coords, orderId, status]);

  const pingLocation = async (silent = false) => {
    if (!token) {
      if (!silent) toast.error('Driver token required');
      return;
    }
    if (!coords) {
      if (!silent) toast.error('Waiting for GPS…');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/commerce/driver/location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          lat: coords.lat,
          lng: coords.lng,
          order_id: orderId || undefined,
          delivery_status: orderId ? status : undefined,
        }),
      });
      if (!res.ok) throw new Error('Ping failed');
      localStorage.setItem(`driver_token_${websiteId}`, token);
      if (!silent) toast.success('Location sent');
    } catch (e) {
      if (!silent) toast.error(e.message || 'Failed to send location');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6">
      <div className="max-w-lg mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Navigation className="w-6 h-6 text-cyan-400" />Driver app</h1>
          <p className="text-zinc-400 text-sm mt-1">Share live GPS with customers on their tracking page.</p>
        </div>

        <input
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Driver token"
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg"
        />
        <input
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="Order ID (optional)"
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg">
          <option value="en_route">En route</option>
          <option value="arrived">Arrived</option>
          <option value="delivered">Delivered</option>
        </select>

        {coords && (
          <div className="text-sm text-zinc-400 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
          </div>
        )}

        <div className="flex gap-3">
          <Button onClick={() => setWatching(true)} variant="outline" className="border-zinc-700 flex-1">Start GPS</Button>
          <Button onClick={() => pingLocation(false)} className="bg-cyan-600 hover:bg-cyan-700 flex-1">Send location</Button>
        </div>
        {watching && (
          <p className="text-xs text-zinc-500">Live mode: sending GPS every 15 seconds while this tab is open.</p>
        )}
      </div>
    </div>
  );
};

export default DriverDeliveryApp;
